import { Host, h } from "@stencil/core";
/**
 * @slot checkedicon - Allows overriding the default checked icon.
 * @slot uncheckedicon - Allows overriding the unchecked icon.
 * @slot intermediateicon - If intermadiate state is used, allows overriding its icon.
 */
export class DnnCheckbox {
    constructor() {
        /** Defines if the checkbox is checked (true) or unchecked (false) or in an intermediate state (undefined) */
        this.checked = "unchecked";
        /** Defines if clicking the checkbox will go through the intermediate state between checked and unchecked (tri-state) */
        this.useIntermediate = false;
        /** The value for this checkbox (not to be confused with its checked state). */
        this.value = "on";
        /** The name to show in the formData (if using forms). */
        this.name = "";
        /** If true, the checkbox needs to be checked for the form validation to succeed. */
        this.required = false;
        /** A function that will be called when the checkbox needs to change state and returns the next state.
         * Can be used to customize the order of the states when the component is clicked.
         * Only called if you also use the tri-state feature (useIntermediate).
         */
        this.nextStateHandler = (currentState) => this.defaultNextStateHandler(currentState);
        /** Can be used to customize the validation message when the field is required but not checked. */
        this.requiredMessage = "The checkbox must be checked";
        this.focused = false;
        this.valid = true;
    }
    handleClick() {
        this.changeState();
    }
    /** Reports the input validity details. See https://developer.mozilla.org/en-US/docs/Web/API/ValidityState */
    async checkValidity() {
        if (this.required && this.checked != "checked") {
            this.valid = false;
        }
        if (!this.valid) {
            this.internals.setValidity({ valueMissing: true }, this.requiredMessage);
        }
        return this.internals.validity;
    }
    componentWillLoad() {
        this.originalChecked = this.checked;
        this.internals.setFormValue(this.checked);
    }
    handleCheckedChange(newValue, oldValue) {
        if (newValue !== oldValue && this.checked == "checked") {
            var data = new FormData();
            data.append(this.name, this.value);
            this.internals.setFormValue(data);
            this.internals.setValidity({});
            this.valid = true;
        }
        if (newValue != "checked" && this.required) {
            this.valid = false;
            this.internals.setValidity({ valueMissing: true }, this.requiredMessage);
        }
    }
    formResetCallback() {
        this.internals.setValidity({});
        this.checked = this.originalChecked;
    }
    defaultNextStateHandler(currentState) {
        switch (currentState) {
            case "checked":
                return "intermediate";
            case "intermediate":
                return "unchecked";
            case "unchecked":
                return "checked";
        }
    }
    changeState() {
        if (!this.useIntermediate) {
            switch (this.checked) {
                case "checked":
                    this.checked = "unchecked";
                    break;
                case "unchecked":
                case "intermediate":
                    this.checked = "checked";
                    break;
                default:
                    break;
            }
            this.checkedchange.emit(this.checked);
            return;
        }
        this.checked = this.nextStateHandler(this.checked);
        this.checkedchange.emit(this.checked);
    }
    getButtonClasses() {
        let classes = `icon ${this.checked}`;
        if (!this.valid) {
            classes += " invalid";
        }
        return classes;
    }
    render() {
        return (h(Host, { key: '49b2d5869f04de1a9944fb564dc4412ac9f10e4d', tabIndex: this.focused ? -1 : 0, onFocus: () => this.button.focus(), onBlur: () => this.button.blur() }, h("button", { key: '8b2429b3d910ff34aa62bd09aa64044f38356fc7', ref: el => this.button = el, onFocus: () => this.focused = true, onBlur: () => this.focused = false, class: this.getButtonClasses() }, h("div", { key: '19ad9e0263b892dbc4a82e9fc415b9653a73bd4b', class: "unchecked" }, h("slot", { key: '94e37e53bbe5edb6f58621e69107218be8d1ad07', name: "uncheckedicon" }, h("svg", { key: '548f8bc35a5c5d733ba9f9ed8612522879b24f3e', xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("path", { key: '9478741e09aceb96d68e8aa5111287b9fc940472', d: "M0 0h24v24H0z", fill: "none" }), h("path", { key: '56b4bb4262e5e9d10d9174c0cad44ff61eba1741', d: "M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" })))), h("div", { key: '532c59227a1c24e6129cc4a755c63b3e33d335a4', class: "checked" }, h("slot", { key: 'd06a535e89b8e280ca22739091a76de0827c0f32', name: "checkedicon" }, h("svg", { key: '2d896ae0582821c8ca47cc113accd9e8e6389f9f', xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("path", { key: '563b605e835d9ec257b9e350021a90eff79fcc58', d: "M0 0h24v24H0z", fill: "none" }), h("path", { key: '5c922a9869dac44d8c994f20f03012bb14df60f2', d: "M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" })))), h("div", { key: 'dce07e94818fcd15cb06f895f3310830e56f5f03', class: "intermediate" }, h("slot", { key: '479043ee32c0c953a2d0c13beea1f83aafad3c12', name: "intermediateicon" }, h("svg", { key: 'd67fc1226be45932ab0604818aee417ed18d503d', class: "undefined", xmlns: "http://www.w3.org/2000/svg", "enable-background": "new 0 0 24 24", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("g", { key: 'a12a7bad9f8f497e8bee3e28bb65b633c77ceaf2' }, h("rect", { key: 'e520a504f3413b3a37ec2ce9f43d83718597a0ed', fill: "none", height: "24", width: "24" })), h("g", { key: '5fc329fb565b4ac5d0d1abd3d397abe44d77692c' }, h("g", { key: '56812a73ece958c4d9a4d2c39f3e6e86abed7329' }, h("g", { key: '2f2dfa3ccd2d9775bdc54082fb760dac9c65287c' }, h("path", { key: 'e3d7949eaf391241f2ee822898ec30bd1e7576a8', d: "M19,3H5C3.9,3,3,3.9,3,5v14c0,1.1,0.9,2,2,2h14c1.1,0,2-0.9,2-2V5C21,3.9,20.1,3,19,3z M17,13H7v-2h10V13z" }))))))))));
    }
    static get is() { return "dnn-checkbox"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["dnn-checkbox.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dnn-checkbox.css"]
        };
    }
    static get properties() {
        return {
            "checked": {
                "type": "string",
                "attribute": "checked",
                "mutable": true,
                "complexType": {
                    "original": "CheckedState",
                    "resolved": "\"checked\" | \"intermediate\" | \"unchecked\"",
                    "references": {
                        "CheckedState": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/dnn-checkbox/types.ts::CheckedState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Defines if the checkbox is checked (true) or unchecked (false) or in an intermediate state (undefined)"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"unchecked\""
            },
            "useIntermediate": {
                "type": "boolean",
                "attribute": "use-intermediate",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Defines if clicking the checkbox will go through the intermediate state between checked and unchecked (tri-state)"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "value": {
                "type": "string",
                "attribute": "value",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value for this checkbox (not to be confused with its checked state)."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"on\""
            },
            "name": {
                "type": "string",
                "attribute": "name",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The name to show in the formData (if using forms)."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"\""
            },
            "required": {
                "type": "boolean",
                "attribute": "required",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the checkbox needs to be checked for the form validation to succeed."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "nextStateHandler": {
                "type": "unknown",
                "attribute": "next-state-handler",
                "mutable": false,
                "complexType": {
                    "original": "(currentState: CheckedState) => CheckedState",
                    "resolved": "(currentState: CheckedState) => CheckedState",
                    "references": {
                        "CheckedState": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/dnn-checkbox/types.ts::CheckedState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A function that will be called when the checkbox needs to change state and returns the next state.\nCan be used to customize the order of the states when the component is clicked.\nOnly called if you also use the tri-state feature (useIntermediate)."
                },
                "getter": false,
                "setter": false,
                "defaultValue": "(currentState) => this.defaultNextStateHandler(currentState)"
            },
            "requiredMessage": {
                "type": "string",
                "attribute": "required-message",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Can be used to customize the validation message when the field is required but not checked."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"The checkbox must be checked\""
            }
        };
    }
    static get states() {
        return {
            "focused": {},
            "valid": {}
        };
    }
    static get events() {
        return [{
                "method": "checkedchange",
                "name": "checkedchange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fires up when the checkbox checked property changes."
                },
                "complexType": {
                    "original": "\"checked\" | \"unchecked\" | \"intermediate\"",
                    "resolved": "\"checked\" | \"intermediate\" | \"unchecked\"",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "checkValidity": {
                "complexType": {
                    "signature": "() => Promise<ValidityState>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ValidityState": {
                            "location": "global",
                            "id": "global::ValidityState"
                        }
                    },
                    "return": "Promise<ValidityState>"
                },
                "docs": {
                    "text": "Reports the input validity details. See https://developer.mozilla.org/en-US/docs/Web/API/ValidityState",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "checked",
                "methodName": "handleCheckedChange"
            }];
    }
    static get listeners() {
        return [{
                "name": "click",
                "method": "handleClick",
                "target": undefined,
                "capture": true,
                "passive": false
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
//# sourceMappingURL=dnn-checkbox.js.map
